/* global __WEB__ */
import { socketConnectionError } from '../../actions/socket';
import { showConnectionError } from '../app';

export default function socketMiddleware() {
    let socket = null;

    let pingInterval;
    let retryConnection = null;
    let retryTimeout;
    let hasSent = false;
    let _promise;
    let url;

    const PING_INTERVAL = 30;
    const RETRY_TIMEOUT = 20;

    const channels = {};
    const channelById = {};
    const onMessageHandlers = {};
    const ready = () => _promise;

    const subscribe = (payload) => {
        const channel = payload.channel;
        const query = payload.query;

        if (!onMessageHandlers[channel]) {
            onMessageHandlers[channel] = payload.onMessage;
        }

        channels[channel] = channel;

        ready().then(ws => {
            if (ws.readyState === WebSocket.OPEN) {
                hasSent = true;
                ws.send(`subscribe?${query}`);
            }
        });
    };

    const unsubscribe = (payload) => {
        const channel = payload.channel;

        // Clean up, so we can subscribe to the symbol again
        if (channels.hasOwnProperty(channel)) {
            delete channels[channel];
        }

        ready().then(ws => {
            if (ws.readyState === WebSocket.OPEN) {
                socket.send(`unsubscribe?channel=${channel}`);
            }
        });
    };

    const resubscribeChannels = () => {
        const channelsToResubscribe = [];

        // Go trough and unsubscribe to each channel first, to properly close things
        // on OMS's end.
        for (const channel in channels) {
            if (channels.hasOwnProperty(channel)) {
                const params = { query: channels[channel], channel };
                channelsToResubscribe.push(params);

                unsubscribe(channel);

                // Delete the channel, so we don't resubscribe to it again.
                delete channels[channel];
            }
        }

        // Re-subscribe to each channel
        channelsToResubscribe.forEach(params => subscribe(params.channel, params.query));
    };

    const onClose = (dispatch) => () => {
        clearInterval(pingInterval);
        clearTimeout(retryTimeout);
        retryTimeout = setTimeout(
          () => retryConnection && retryConnection(dispatch), 1000 * RETRY_TIMEOUT);
    };

    const onMessage = () => event => {
        // Ignored messages
        const ignoredMessages = [
            'pong',
            'ack_subscribe',
            'ack_unsubscribe',
        ];

        // Exit if the event data is listed among the ignored messages
        if (ignoredMessages.indexOf(event.data) > -1) return;

        try {
            // Save the OMS-generated channel id
            const data = JSON.parse(event.data);
            if (typeof data.new === 'boolean') {
                channelById[data.id] = data.channel;
                return;
            }

            // @TODO Create singular or type separated messageHandlers
            // Run appropriate message handler based on channel
            const onMessageHandler = onMessageHandlers[channelById[data.id]];
            if (onMessageHandler && typeof onMessageHandler === 'function') {
                onMessageHandler(data);
            }
        } catch (e) {
          // ignore for now
        }
    };

    const connect = (payload, dispatch, reconnectAttempts = 0) => {
        const { CLOSED } = WebSocket;
        if (!payload.url || socket !== null && socket.readyState !== CLOSED) {
            // Already connected
            return false;
        }

        url = payload.url;

        // @TODO Complete fallbackAction implementation.
        // Dispatch fallbackAction (if available) for browsers that does not support Websockets
        if (
            typeof payload.fallbackAction === 'function' &&
            typeof window.WebSocket !== 'function'
        ) {
            dispatch(payload.fallbackAction);
            return false;
        }

        // Promise that is resolved when connection is opened
        _promise = new Promise((resolve) => {
            socket = new WebSocket(url);
            socket.onmessage = onMessage();
            socket.onopen = () => {
                // Clear ping interval in case this is a reconnect
                clearInterval(pingInterval);

                // Start ping interval
                pingInterval = setInterval(() => socket.send('ping'), 1000 * PING_INTERVAL);

                if (hasSent) {
                    resubscribeChannels();
                }

                resolve(socket);
            };
            socket.onclose = onClose();
            socket.onerror = () => {
                socket.close();
                socket = null;
                dispatch(socketConnectionError({
                    error: 'Could not connect to socket',
                    reconnectAttempts: reconnectAttempts + 1,
                    reconnect: reconnectAttempts < 5,
                }));
            };
        });

        return true;
    };

    retryConnection = (dispatch) => {
        const { CLOSED } = WebSocket;
        if (
            (socket === null || socket.readyState === CLOSED) &&
            Object.keys(channels).length > 0 &&
            url
        ) {
            connect({ url }, dispatch);
        }
    };

    const onConnectionError = ({ reconnect, reconnectAttempts }, dispatch) => {
        if (reconnect) {
            setTimeout(() => connect({ url }, dispatch, reconnectAttempts), 1000);
        }
    };

    /* HANDLE RELEVANT ACTIONS */
    return ({ dispatch }) => (next) => (action) => {
        const payload = action.payload;
        switch (action.type) {
        case '@@socket/CONNECT': {
            // connect and stop action-progagation if already connected
            if (!connect(payload, dispatch)) {
                return null;
            }
            break;
        }

        case '@@socket/SUBSCRIBE': {
            // if channel is not set, stop action-progagation
            if (!payload.channel) return null;

            subscribe(payload);
            break;
        }

        case '@@socket/UNSUBSCRIBE': {
            // if channel is not set, stop action-progagation
            if (!payload.channel || !channels[payload.channel]) return null;

            unsubscribe(payload);
            break;
        }

        case '@@socket/CONNECTION_ERROR': {
            if (!payload.reconnect) {
                dispatch(showConnectionError(payload.error));
                break;
            }
            onConnectionError(payload, dispatch);
            break;
        }

        default:
            return next(action);
        }

        return next(action);
    };
}



/** WEBPACK FOOTER **
 ** /src/shared/redux/middlewares/socketMiddleware.js
 **/